// LearnModeScreen — kid-initiated Q&A with Finn.
// Chat-like thread: kid questions on the right, Finn's answers on the left.
// Large tap targets (≥60px), auto-TTS, Web Speech STT mic, daily-cap banner.

import { useEffect, useRef, useState } from 'react';
import { askFinn, getLearnHistory, getThementrainer } from '../api/client';
import type { LearnAskResponse, LearnHistoryItem } from '../api/types';
import { useTTS } from '../hooks/useTTS';
import { useSTT } from '../hooks/useSTT';
import { useAudioFirst } from '../hooks/useAudioFirst';
import AdventurerIllustration from '../components/adventurer/AdventurerIllustration';
import CharacterIcon from '../components/adventurer/CharacterIcon';
import { ADVENTURERS, type AdventurerId } from '../components/adventurer/AdventurerStamp';

interface Props {
  adventurerId: AdventurerId;
  onBack: () => void;
}

interface Turn {
  id: string;
  role: 'kid' | 'finn';
  text: string;
  tier?: 1 | 2 | 3;
  confidence?: 'high' | 'low';
  deflected?: boolean;
}

export default function LearnModeScreen({ adventurerId, onBack }: Props) {
  const adv = ADVENTURERS[adventurerId];
  const [turns, setTurns] = useState<Turn[]>([]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [capLeft, setCapLeft] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [scope, setScope] = useState<string | null>(null);
  const threadRef = useRef<HTMLDivElement | null>(null);

  const tts = useTTS();
  const stt = useSTT();
  const audioFirst = useAudioFirst();

  // Bypass the tts.enabled gate when Audio-First is on: the kid can't read.
  const speakAnswer = (text: string) => {
    if (tts.enabled) { tts.speak(text); return; }
    if (audioFirst.enabled && tts.available) {
      try {
        const u = new SpeechSynthesisUtterance(text);
        u.lang = 'de-DE';
        u.rate = 0.95;
        u.pitch = 1.05;
        window.speechSynthesis.cancel();
        window.speechSynthesis.speak(u);
      } catch { /* ignore */ }
    }
  };

  // On mount, hydrate from server history (newest first; reverse for chat-chron).
  useEffect(() => {
    let alive = true;
    getLearnHistory(20)
      .then(({ items, remaining_today }) => {
        if (!alive) return;
        setCapLeft(remaining_today);
        const hydrated: Turn[] = [];
        // items are newest-first; reverse so we show oldest→newest in the scroll.
        [...items].reverse().forEach((it: LearnHistoryItem) => {
          hydrated.push({ id: `k-${it.pair_id}`, role: 'kid', text: it.question_text });
          hydrated.push({
            id: `f-${it.pair_id}`,
            role: 'finn',
            text: it.answer_text,
            tier: it.tier,
            confidence: it.confidence,
            deflected: it.deflected,
          });
        });
        setTurns(hydrated);
      })
      .catch(() => { /* history optional — ignore */ });
    getThementrainer()
      .then(sc => { if (alive) setScope(sc.topic_label); })
      .catch(() => { /* scope optional — ignore */ });
    return () => { alive = false; };
  }, []);

  // Scroll-to-bottom on new turns.
  useEffect(() => {
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [turns.length, busy]);

  // Surface the STT transcript into the draft as the kid speaks.
  useEffect(() => {
    if (stt.transcript) setDraft(stt.transcript);
  }, [stt.transcript]);

  const ask = async () => {
    const q = draft.trim();
    if (!q || busy) return;
    setBusy(true);
    setError(null);
    const kidTurn: Turn = { id: `k-${Date.now()}`, role: 'kid', text: q };
    setTurns(t => [...t, kidTurn]);
    setDraft('');
    stt.reset();
    try {
      const res: LearnAskResponse = await askFinn(q);
      const finnTurn: Turn = {
        id: `f-${res.pair_id}`,
        role: 'finn',
        text: res.answer.text,
        tier: res.tier,
        confidence: res.answer.confidence,
        deflected: res.deflected,
      };
      setTurns(t => [...t, finnTurn]);
      setCapLeft(res.remaining_today);
      speakAnswer(res.answer.text);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  const canAsk = !!draft.trim() && !busy && (capLeft === null || capLeft > 0);
  const capDepleted = capLeft === 0;

  return (
    <div style={{
      display: 'flex', flexDirection: 'column',
      height: '100dvh', background: 'var(--parchment)',
      fontFamily: 'var(--sans)',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '18px 20px',
        borderBottom: '1px solid rgba(60,50,40,0.12)',
        background: 'var(--card)',
      }}>
        <button
          onClick={onBack}
          aria-label="Zurück zum Abenteuer"
          style={{
            minHeight: 44, padding: '10px 16px',
            background: 'none', border: '1px solid rgba(60,50,40,0.2)',
            borderRadius: 3,
            fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.16em',
            color: 'var(--ink-muted)', textTransform: 'uppercase',
            cursor: 'pointer',
          }}
        >← Zurück</button>
        <div style={{
          fontFamily: 'var(--display)', fontSize: 22, fontWeight: 600,
          color: 'var(--ink)', letterSpacing: '-0.01em',
        }}>
          <CharacterIcon id={adventurerId} size={20} />
          <span style={{ marginLeft: 8, verticalAlign: 'middle' }}>Frag {adv.name}</span>
        </div>
        <button
          onClick={tts.toggle}
          aria-label={tts.enabled ? 'Vorlesen aus' : 'Vorlesen an'}
          disabled={!tts.available}
          style={{
            minWidth: 44, minHeight: 44,
            background: tts.enabled ? 'var(--amber-soft)' : 'none',
            border: '1px solid rgba(60,50,40,0.2)',
            borderRadius: 3,
            fontSize: 18,
            cursor: tts.available ? 'pointer' : 'default',
            opacity: tts.available ? 1 : 0.4,
          }}
        >{tts.enabled ? '🔊' : '🔈'}</button>
      </div>

      {/* Thementrainer scope banner */}
      {scope && (
        <div role="status" style={{
          margin: '0 20px',
          padding: '10px 14px',
          background: 'var(--amber-soft)',
          border: '1px solid rgba(201,106,47,0.3)',
          borderRadius: 4,
          fontFamily: 'var(--serif)', fontSize: 14,
          color: 'var(--amber-deep)',
        }}>
          <span style={{
            fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.18em',
            textTransform: 'uppercase', marginRight: 8,
          }}>Thementrainer</span>
          Heute üben wir zusammen: <strong>{scope}</strong>.
        </div>
      )}

      {/* Cap banner (only shown when near or at cap) */}
      {capLeft !== null && capLeft <= 1 && (
        <div style={{
          padding: '10px 20px',
          background: capDepleted ? 'rgba(191,90,90,0.12)' : 'var(--amber-soft)',
          borderBottom: '1px solid rgba(60,50,40,0.1)',
          fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.08em',
          color: 'var(--ink-muted)', textAlign: 'center',
        }}>
          {capDepleted
            ? 'Finn muss kurz Luft holen — morgen kannst du ihn wieder fragen!'
            : `Noch ${capLeft} Frage${capLeft === 1 ? '' : 'n'} heute`}
        </div>
      )}

      {/* Thread */}
      <div
        ref={threadRef}
        style={{
          flex: 1, overflowY: 'auto',
          padding: '24px 20px 16px',
          display: 'flex', flexDirection: 'column', gap: 16,
        }}
      >
        {turns.length === 0 && !busy && (
          <EmptyState adventurerId={adventurerId} />
        )}
        {turns.map(t => (
          <Bubble key={t.id} turn={t} adventurerId={adventurerId} />
        ))}
        {busy && (
          <div style={{
            alignSelf: 'flex-start', maxWidth: '80%',
            padding: '12px 16px',
            background: 'var(--card)',
            border: '1px solid rgba(60,50,40,0.12)',
            borderRadius: 16, borderBottomLeftRadius: 3,
            fontFamily: 'var(--serif)', fontSize: 16, fontStyle: 'italic',
            color: 'var(--ink-muted)',
          }}>
            Finn denkt nach…
          </div>
        )}
      </div>

      {/* Input row */}
      <div style={{
        padding: '14px 20px 20px',
        borderTop: '1px solid rgba(60,50,40,0.12)',
        background: 'var(--card)',
      }}>
        {error && (
          <div role="alert" style={{
            marginBottom: 10, fontFamily: 'var(--mono)', fontSize: 11,
            color: 'var(--wrong)', letterSpacing: '0.04em',
          }}>{error}</div>
        )}
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {stt.available && (
            <button
              onMouseDown={stt.start}
              onMouseUp={stt.stop}
              onTouchStart={(e) => { e.preventDefault(); stt.start(); }}
              onTouchEnd={(e) => { e.preventDefault(); stt.stop(); }}
              aria-label={stt.listening ? 'Aufnahme stoppen' : 'Sprechen halten'}
              style={{
                minWidth: 60, minHeight: 60,
                borderRadius: '50%',
                background: stt.listening ? 'var(--wrong)' : 'var(--amber)',
                color: '#fff', border: 'none',
                fontSize: 24, cursor: 'pointer',
                boxShadow: stt.listening
                  ? '0 0 0 4px rgba(191,90,90,0.25)'
                  : '0 4px 12px rgba(201,106,47,0.35)',
                transition: 'all 0.15s',
              }}
            >🎙</button>
          )}
          <input
            value={draft}
            onChange={e => { setDraft(e.target.value); setError(null); }}
            onKeyDown={e => { if (e.key === 'Enter' && canAsk) ask(); }}
            disabled={busy || capDepleted}
            placeholder={capDepleted ? 'Morgen wieder!' : `Frag ${adv.name} alles…`}
            aria-label="Frage an Finn"
            style={{
              flex: 1, minHeight: 60,
              padding: '14px 18px',
              background: 'var(--parchment)',
              border: '2px solid rgba(60,50,40,0.2)',
              borderRadius: 4,
              fontFamily: 'var(--serif)', fontSize: 18,
              color: 'var(--ink)', outline: 'none',
              boxSizing: 'border-box',
            }}
          />
          <button
            onClick={ask}
            disabled={!canAsk}
            aria-label="Frage senden"
            style={{
              minHeight: 60, padding: '0 22px',
              background: canAsk ? 'var(--amber)' : 'rgba(60,50,40,0.1)',
              color: canAsk ? '#fff' : 'var(--ink-muted)',
              border: 'none', borderRadius: 3,
              fontFamily: 'var(--sans)', fontSize: 16, fontWeight: 700,
              cursor: canAsk ? 'pointer' : 'default',
            }}
          >Fragen →</button>
        </div>
      </div>
    </div>
  );
}

function EmptyState({ adventurerId }: { adventurerId: AdventurerId }) {
  const suggestions = [
    'Wieso ist der Himmel blau?',
    'Wie schreibt man „Schmetterling"?',
    'Warum haben Eichhörnchen so dicke Schwänze?',
    'Was ist ein Adjektiv?',
  ];
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      gap: 20, padding: '32px 16px', textAlign: 'center',
    }}>
      <AdventurerIllustration id={adventurerId} size={140} beat="idle" />
      <div style={{
        fontFamily: 'var(--display)', fontSize: 24, color: 'var(--ink)',
        letterSpacing: '-0.01em',
      }}>Was möchtest du wissen?</div>
      <div style={{
        fontFamily: 'var(--serif)', fontSize: 15, fontStyle: 'italic',
        color: 'var(--ink-muted)', maxWidth: 380, lineHeight: 1.5,
      }}>
        Finn weiß viel über die Welt und über deine Schule. Frag ihn, was du willst — er gibt dir sein Bestes.
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%', maxWidth: 380 }}>
        {suggestions.map(s => (
          <div key={s} style={{
            padding: '10px 14px',
            background: 'var(--card)',
            border: '1px dashed rgba(60,50,40,0.2)',
            borderRadius: 6,
            fontFamily: 'var(--serif)', fontSize: 14,
            color: 'var(--ink-soft)',
          }}>„{s}"</div>
        ))}
      </div>
    </div>
  );
}

function Bubble({ turn, adventurerId }: { turn: Turn; adventurerId: AdventurerId }) {
  const adv = ADVENTURERS[adventurerId];
  const isKid = turn.role === 'kid';
  const tierColor = turn.tier === 1
    ? 'rgba(191,90,90,0.12)'
    : turn.tier === 2
      ? 'rgba(200,150,60,0.12)'
      : undefined;

  return (
    <div style={{
      display: 'flex', alignItems: 'flex-end',
      justifyContent: isKid ? 'flex-end' : 'flex-start',
      gap: 8,
    }}>
      {!isKid && (
        <div style={{ flexShrink: 0, width: 36, height: 36, opacity: 0.9 }}>
          <AdventurerIllustration id={adventurerId} size={36} beat="idle" />
        </div>
      )}
      <div style={{
        maxWidth: '78%',
        padding: '12px 16px',
        background: isKid ? 'var(--amber-soft)' : (tierColor || 'var(--card)'),
        border: `1px solid ${isKid ? 'var(--amber)' : 'rgba(60,50,40,0.12)'}`,
        borderRadius: 16,
        borderBottomRightRadius: isKid ? 3 : 16,
        borderBottomLeftRadius: isKid ? 16 : 3,
        fontFamily: 'var(--serif)', fontSize: 16,
        lineHeight: 1.5, color: 'var(--ink)',
        whiteSpace: 'pre-wrap',
      }}>
        {turn.text}
        {!isKid && turn.deflected && (
          <div style={{
            marginTop: 6, fontFamily: 'var(--mono)', fontSize: 10,
            letterSpacing: '0.12em', textTransform: 'uppercase',
            color: 'var(--ink-muted)',
          }}>Finn hat das an Mama oder Papa weitergeleitet</div>
        )}
        {!isKid && turn.confidence === 'low' && !turn.deflected && (
          <div style={{
            marginTop: 6, fontFamily: 'var(--mono)', fontSize: 10,
            letterSpacing: '0.12em', textTransform: 'uppercase',
            color: 'var(--ink-muted)',
          }}>{adv.name} ist sich nicht ganz sicher</div>
        )}
      </div>
    </div>
  );
}
